import { Button, Card, Pagination, PermissionGate, Table, TableColumn } from '@/components/common';
import { usePermissions } from '@/hooks';
import { InstitutionDTO, PaginationMetadata } from '@/models';
import { resolveAssetUrl } from '@/utils/resolveAssetUrl';
import React from 'react';

interface InstitutionTableProps {
  institutions: InstitutionDTO[];
  pagination: PaginationMetadata | null;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (inst: InstitutionDTO) => void;
  onDelete: (inst: InstitutionDTO) => void;
}

export const InstitutionTable: React.FC<InstitutionTableProps> = ({
  institutions,
  pagination,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}) => {
  const { canPerform } = usePermissions();

  const renderLogo = (institution: InstitutionDTO) => {
    const logoUrl = resolveAssetUrl(institution.logoIconUrl || institution.logoFullUrl);

    if (!logoUrl) {
      return <span className="text-muted">-</span>;
    }

    return (
      <img
        src={logoUrl}
        alt={`${institution.name || 'Institution'} logo`}
        className="rounded border bg-white"
        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
      />
    );
  };

  const columns: TableColumn<InstitutionDTO>[] = [
    {
      key: 'logo',
      header: 'Logo',
      width: '72px',
      render: renderLogo,
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (inst) => inst.name || '-',
    },
    {
      key: 'city',
      header: 'City',
      render: (inst) => inst.city || '-',
    },
    {
      key: 'province',
      header: 'Province',
      render: (inst) => inst.province || '-',
    },
    {
      key: 'phoneNumber',
      header: 'Phone',
      render: (inst) => inst.phoneNumber || '-',
    },
    {
      key: 'email',
      header: 'Email',
      render: (inst) => inst.email || '-',
    },
    ...(canPerform('institution:edit') || canPerform('institution:delete')
      ? [
          {
            key: 'actions' as const,
            header: 'Actions',
            width: '150px',
            render: (inst: InstitutionDTO) => (
              <div className="d-flex gap-2">
                <PermissionGate action="institution:edit">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-edit"
                    onClick={() => onEdit(inst)}
                  >
                    Edit
                  </Button>
                </PermissionGate>
                <PermissionGate action="institution:delete">
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fas fa-trash"
                    onClick={() => onDelete(inst)}
                  />
                </PermissionGate>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card className="border-0 shadow-sm">
      <Table
        columns={columns}
        data={institutions}
        keyExtractor={(inst) => inst.id}
        isLoading={isLoading}
        emptyMessage="No institutions found. Click 'Add Institution' to create one."
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && (
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
              institutions
            </small>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
};
